import * as React from 'react';
import { useEffect, useState } from 'react';
import { IUser } from '../../../model/user/IUser';
import { Button, Col, Dropdown, DropdownButton, DropdownItem, Form, Row, Table } from 'react-bootstrap';
import { FaTimes, FaUser } from 'react-icons/fa';
import { AgencyService } from '../AgencyService';
import { useNavigate } from 'react-router';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { ErrorToast, SuccessToast } from '../../../utils/toasters';
import Flag from '../../../shared/components/Flag';
import TableLayout from '../../../shared/layout/table/TableLayout';
import { ITableColumn } from '../../../shared/components/TableSkeleton';
import { useDispatch } from 'react-redux';
import generalSlice from '../../../store/general/GeneralSlice';
import { formatDate } from '../../../utils/Functions';
import SingleSelectDropdown from '../../../shared/components/SingleSelectDropdown';

const PENDING_COLUMNS: ITableColumn[] = [
    { label: 'ID' },
    { label: 'Requested date' },
    { label: 'Email' },
    { label: 'Influencer Name' },
    { label: 'Requested Agency Name' },
    { label: 'Current Agency Name' },
    { label: 'Action' },
];

export function PendingInfluencers() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [state, setState] = useState({
        perPage: 15,
        page: 1,
        hasExisting: 'true',
    });

    const [agencies, setAgencies] = useState<any[]>([]);
    const [change, setChange] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(0);

    useEffect(() => {
        setIsLoading(true);
        AgencyService.getPendingInfluencers(state)
            .then(data => {
                setAgencies(data.data);
                setTotalPages(Math.ceil(data.count / state.perPage));
            })
            .catch(err => ErrorToast(err))
            .finally(() => setIsLoading(false));
    }, [change]);
    function handleClick() {
        setChange(!change);
    }

    const handlePages = (updatePage: number) => {
        setState({ ...state, page: updatePage });
        setChange(!change);
    };

    function handleChange(evt: any) {
        const value = evt.target.value;
        setState({
            ...state,
            [evt.target.name]: value?.trim(),
        });
    }

    const deleteInfluencer = (influencerId: number) => {
        AgencyService.removePendingInfluencer(influencerId)
            .then(() => {
                SuccessToast('Successfully deleted influencer');
                dispatch(
                    generalSlice.actions.setConfirmModal({
                        show: false,
                    })
                );
                setChange((prev: boolean) => !prev);
            })
            .catch(err => ErrorToast(err));
    };
    return (
        <>
            <div
                className="card mb-5 p-4 mt-3"
                onKeyDown={e => {
                    if (e.key === 'Enter') {
                        handleClick();
                        setState({ ...state, page: 1 });
                    }
                }}
            >
                <Row>
                    <Form.Group as={Col} md={3} xs={6}>
                        <Form.Control
                            placeholder="ID"
                            className="autocomplete-input"
                            name="id"
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Form.Group as={Col} md={3} xs={6}>
                        <SingleSelectDropdown
                            placeholder="Has Existing?"
                            options={[
                                { value: 'true', label: 'Existing' },
                                { value: 'false', label: 'Not Existing' },
                            ]}
                            selected={
                                state.hasExisting && state.hasExisting !== 'undefined' ? String(state.hasExisting) : ''
                            }
                            onChange={value =>
                                handleChange({ target: { name: 'hasExisting', value: value || 'undefined' } })
                            }
                        />
                    </Form.Group>
                </Row>

                <div className="mt-4">
                    <Button
                        type="submit"
                        onClick={() => {
                            handleClick();
                            setState({ ...state, page: 1 });
                        }}
                    >
                        Search
                    </Button>
                </div>
            </div>

            <TableLayout
                pagination={{ page: state.page, totalPages: totalPages }}
                handlePages={handlePages}
                listLength={agencies.length}
                dataName="agency"
                skeletonColumns={PENDING_COLUMNS}
                skeletonRows={state.perPage}
                isLoading={isLoading}
            >
                <Table hover responsive>
                    <thead>
                        <tr>
                            {PENDING_COLUMNS.map(column => (
                                <th key={column.label} className={column.className}>
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {agencies?.map(e => (
                            <tr key={e.id}>
                                {/* ID */}
                                <td>{e.id}</td>
                                <td>{formatDate(e.createdAt)}</td>
                                <td>{e.email}</td>

                                {/* Influencer name */}
                                <td>
                                    {e?.isExisting === true && <ImpersonateEye id={e?.influencer?.id ?? 0} />}
                                    {e?.influencer?.displayName ??
                                        `${e?.influencer?.firstName ?? ''} ${e?.influencer?.lastName ?? ''}`}
                                </td>

                                {/* Requested agency */}
                                <td>{e?.agency?.displayName ?? '-'}</td>

                                {/* Current agency */}

                                <td>
                                    {e?.influencer?.agencyCompany?.displayName ? (
                                        <span className="word-break">{e?.influencer?.agencyCompany?.displayName}</span>
                                    ) : (
                                        <span className="word-break text-danger">
                                            <FaTimes />
                                        </span>
                                    )}
                                </td>
                                {/* Action */}
                                <td>
                                    <DropdownButton title="Actions">
                                        {e?.isExisting === true && (
                                            <DropdownItem
                                                onClick={() => navigate(`/user/edit/${e?.influencer?.id}/true`)}
                                            >
                                                Edit
                                            </DropdownItem>
                                        )}
                                        {e?.isExisting === true && <Dropdown.Divider></Dropdown.Divider>}

                                        <Dropdown.Item
                                            className="bg-danger-o-50"
                                            onClick={() => {
                                                dispatch(
                                                    generalSlice.actions.setConfirmModal({
                                                        show: true,
                                                        contentBody: `Are you sure to delete ${e.influencer?.displayName}?`,
                                                        action: () => deleteInfluencer(e.id),
                                                    })
                                                );
                                            }}
                                        >
                                            Delete
                                        </Dropdown.Item>
                                    </DropdownButton>
                                </td>
                            </tr>
                        ))}
                    </tbody>

                    {agencies.length < 15 && !!agencies.length && <div className="h-180px"></div>}
                </Table>
            </TableLayout>
        </>
    );
}
